import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/header/Header';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { useDashboard } from '@/components/dashboard/core/DashboardContext';
import { DashboardHeader } from '@/components/dashboard/core/DashboardHeader';
import { DashboardContent } from '@/components/dashboard/core/DashboardContent';
import { UploadQueueModal } from '@/components/upload/UploadQueueModal';
import { CreateFolderModal } from '@/components/hierarchical-folders/CreateFolderModal';
import { VideoSelectorModal } from '@/components/hierarchical-folders/VideoSelectorModal';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { toast } from 'sonner';
import { t } from '@/utils/i18n';

interface DashboardMainContentProps {
  folderId?: string | null;
}

export function DashboardMainContent({ folderId }: DashboardMainContentProps) {
  useLanguage();
  const { isUploadOpen, setIsUploadOpen } = useDashboard();
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isVideoSelectorOpen, setIsVideoSelectorOpen] = useState(false);
  
  const navigate = useNavigate();
  const { hasPermission } = useCustomAuth();
  const { recalculateFolderCounts, addVideo, videos } = useVideoDashboardStore(state => ({
    recalculateFolderCounts: state.recalculateFolderCounts,
    addVideo: state.addVideo,
    videos: state.videos
  }));
  
  const {
    rootFolders,
    currentFolder,
    isLoading,
    fetchRootFolders,
    fetchFolderDetails
  } = useHierarchicalFoldersStore();
  
  // Check if user has edit permission for videos
  const canEdit = hasPermission('videos', 'edit');
  
  // Load data based on current view (root or folder)
  useEffect(() => {
    if (folderId) {
      fetchFolderDetails(folderId);
    } else {
      fetchRootFolders();
    }
  }, [folderId, fetchRootFolders, fetchFolderDetails]);
  
  // Calculate folder counts on mount and when videos change
  useEffect(() => {
    console.log('DashboardMainContent mounted, videos count:', videos.length);
    recalculateFolderCounts();
  }, [recalculateFolderCounts, videos.length]);
  
  const handleNewVideoClick = () => {
    console.log('New video button clicked - redirecting to /new-video');
    navigate('/new-video');
  };
  
  const handleUploadClick = () => {
    console.log('Upload button clicked');
    setIsUploadOpen(true);
  };
  
  const handleNewFolderClick = () => {
    setIsFolderModalOpen(true);
  };
  
  const handleAddVideosClick = () => {
    setIsVideoSelectorOpen(true);
  };

  const handleFileSelect = (file: File) => {
    console.log('File selected:', file.name);
    setIsUploadOpen(false);
  };
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      <Header 
        title={!currentFolder ? translateInterfaceText("Meus Vídeos") : undefined}
        subtitle={!currentFolder ? translateInterfaceText("Gerencie e visualize seus últimos vídeos") : undefined}
      />
      
      {/* Divider contínuo */}
      <div className="hidden lg:block h-px bg-border w-full" />
      
      <main className="flex-1 overflow-y-auto scrollbar-blue feed-scroll-container">
        <div className="max-w-[1280px] mx-auto w-full pl-6 pr-8 pt-10 pb-6 space-y-8">
          <DashboardHeader
            currentFolder={currentFolder}
            onNewFolderClick={handleNewFolderClick}
            onNewVideoClick={handleNewVideoClick}
            onAddVideosClick={folderId ? handleAddVideosClick : undefined}
            isReadOnly={!canEdit}
          />
          
          <DashboardContent
            folderId={folderId}
            currentFolder={currentFolder}
            rootFolders={rootFolders}
            isLoading={isLoading}
            onUploadClick={handleUploadClick} 
            onFileSelect={handleFileSelect}
            onAddVideosClick={folderId ? handleAddVideosClick : undefined}
            isReadOnly={!canEdit}
          />
        </div>
      </main>
      
      <UploadQueueModal
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        targetFolderId={folderId || null}
      />
      
      <CreateFolderModal
        open={isFolderModalOpen}
        onOpenChange={setIsFolderModalOpen}
        parentId={folderId || null}
        onSuccess={(newFolderId) => {
          if (!folderId) {
            // Root: navigate to the created folder
            navigate(`/meus-videos?folderId=${newFolderId}`);
          } else {
            // Subfolder: stay and show toast
            toast.success(translateInterfaceText("Pasta criada."));
          }
        }}
      />
      
      {folderId && (
        <VideoSelectorModal
          open={isVideoSelectorOpen}
          onOpenChange={setIsVideoSelectorOpen}
          folderId={folderId}
          onOpenUploadQueue={() => {
            setIsVideoSelectorOpen(false);
            setIsUploadOpen(true);
          }}
        />
      )}
    </div>
  );
}